<script setup lang="ts">
import { SUPPORTED_MIME_TYPES } from '@/backend/types'
import { startFilePickerSession } from '@/modules/Workspaces/KnowledgeHub/Integrations/startFilePickerSession'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { toast } from '@/shared/toast'
import { getMimeType, getSupportedFileExtension } from '@/shared/utils/file'
import CircularProgress from '@/uiKit/CircularProgress.vue'
import ModalDialog from '@/uiKit/ModalDialog.vue'
import { useEventListener } from '@vueuse/core'
import { onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { activateIframe, getPickerChannelId } from './apiUtils'
import type { SharePointFile } from './types'
import { getFilePickerUrl, getMsalToken } from './utils'

const props = defineProps<{
  open: boolean
  connectionId: string
  maxItems: number
}>()

const emit = defineEmits<{ close: []; files: [SharePointFile[]] }>()

const { workspaceId } = useWorkspaceContext()

const iframeRef = useTemplateRef<HTMLIFrameElement>('iframeRef')

const IFRAME_NAME = 'sharepoint-picker-frame'

let messagePort: MessagePort | undefined = undefined

const channelId = getPickerChannelId()

export type FilePickerItem = {
  id: string
  name: string
  file?: { mimeType: string }
  size?: number
  webUrl?: string
  parentReference: { driveId: string }
  '@sharePoint.endpoint': string
}

const toSharePointFile = (file: FilePickerItem): SharePointFile => {
  const driveId = file.parentReference.driveId

  return {
    id: `${driveId}/${file.id}`,
    name: file.name,
    mimeType: file.file?.mimeType || 'application/octet-stream',
    webUrl: file.webUrl,
    size: file.size,
    parentReference: file.parentReference,
    '@sharePoint.endpoint': file['@sharePoint.endpoint'],
  }
}

const isLoading = ref(true)

/**
 * Handles 'message' event on the window object and
 * sets up listening on the port for the iframe-window message channel
 */
const initializeMessageListener = (event: MessageEvent) => {
  // Validate the message is for us and from the iframe
  if (
    event.source === iframeRef.value?.contentWindow &&
    event.data.type === 'initialize' &&
    event.data.channelId === channelId
  ) {
    messagePort = event.ports[0]
    if (messagePort) {
      // eslint-disable-next-line internal/no-call-composable-outside-setup
      useEventListener(messagePort, 'message', channelMessageListener)
      messagePort.start()
      messagePort.postMessage({ type: 'activate' })
    }
  }
}

/**
 * Handless messages via the iframe-window message channel
 * This is the actual communication between the file picker and the app
 */
const channelMessageListener = async (message: MessageEvent): Promise<void> => {
  const payload = message.data

  // we get this when the iframe has fully loaded
  if (payload.type === 'notification' && payload.data.notification === 'page-loaded') {
    isLoading.value = false
  }

  // expect for the page-loaded notification, all other messages must be commands
  if (payload.type !== 'command') {
    messagePort?.postMessage({
      type: 'result',
      id: payload.id,
      data: {
        result: 'error',
        error: { code: 'unsupportedCommand', message: payload.data.command },
      },
    })

    return
  }

  // if we got here, it's a command

  // all commands must be acknowledged
  messagePort?.postMessage({ type: 'acknowledge', id: message.data.id })

  // user clicked the "cancel" button in the picker
  if (payload.data.command === 'close') {
    close()
  }

  if (payload.data.command === 'authenticate') {
    // picker heuristics asked the user to re-authenticate
    try {
      const token = await getMsalToken(payload.data)

      messagePort?.postMessage({
        type: 'result',
        id: message.data.id,
        data: { result: 'token', token: token },
      })
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'

      // Provide user-friendly error messages
      if (errorMsg.includes('user_cancelled')) {
        toast.error(
          'Authentication was cancelled. Please try again and complete the Microsoft login.',
        )
      } else if (errorMsg.includes('popup')) {
        toast.error('Popup blocked. Please allow popups for this site and try again.')
      }

      messagePort?.postMessage({
        type: 'result',
        id: message.data.id,
        data: {
          result: 'error',
          error: { code: 'unableToObtainToken', message: errorMsg },
        },
      })
    }
  }

  if (payload.data.command === 'pick') {
    const result: { items: FilePickerItem[] } = payload.data
    const selectedFiles = result.items || []

    // filter to supported file types using proper MIME type detection
    const supportedFiles = selectedFiles.filter((file) => {
      const mimeType = getMimeType(getSupportedFileExtension(file.name))
      return (SUPPORTED_MIME_TYPES as string[]).includes(mimeType)
    })

    // let the picker know that the pick command was handled (required)
    messagePort?.postMessage({ type: 'result', id: payload.id, data: { result: 'success' } })

    if (supportedFiles.length === 0) {
      toast.warning(
        `No supported files were selected (${selectedFiles.length} files had unsupported types)`,
      )
    } else {
      emit('files', supportedFiles.map(toSharePointFile))
    }

    close()

    return
  }
}

useEventListener(window, 'message', initializeMessageListener)

onMounted(async () => {
  const { accessToken } = await startFilePickerSession({
    connectionId: props.connectionId,
    integrationId: 'sharepoint_online',
    workspaceId: workspaceId.value,
  })

  const url = await getFilePickerUrl({ accessToken, channelId, maxItems: props.maxItems })
  activateIframe(IFRAME_NAME, url, accessToken)
})

const cleanup = () => {
  messagePort?.removeEventListener('message', channelMessageListener)
  messagePort?.close()
  messagePort = undefined
}

const close = () => {
  cleanup()
  emit('close')
}

onUnmounted(() => cleanup())
</script>

<template>
  <ModalDialog
    class="max-h-[90vh] w-auto max-w-[1200px] rounded-none bg-[#f5f5f5]"
    :open="open"
    aria-label="SharePoint File Picker"
    @close="close"
  >
    <div class="relative flex w-full items-center justify-center">
      <div
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center"
      >
        <CircularProgress size="lg" />
      </div>
      <iframe
        ref="iframeRef"
        class="min-h-[600px] flex-1 border-none"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        :name="IFRAME_NAME"
      >
      </iframe>
    </div>
  </ModalDialog>
</template>
